import React, { useState, useEffect } from 'react';
import { MemoryPlace } from '../../services/memoryService';
import { getAllModules, getUrlType, ModuleItem, moduleSupportsDetailPages, getModuleServiceType, getModuleIdField, parseDetailUrl } from '../../utils/memoryPalaceUtils';
import { peopleService, Person } from '../../services/peopleService';
import { NovelService, Novel } from '../../services/novelService';
import { NotesService, Note } from '../../services/notesService';
import { KnowledgeCategoryService, KnowledgeCategory } from '../../services/knowledgeCategoryService';
import { bookService } from '../../services/bookService';
import { Book } from '../../types/Book';
import { useAuth } from '../../contexts/AuthContext';
import './MemoryPalace.css';

interface PlaceFormProps {
  place?: MemoryPlace | null;
  onSave: (placeData: { name: string; description?: string; properties?: Record<string, any> | string; is_root?: boolean }) => void;
  onCancel: () => void;
  fromPlaceId?: number | null;
  autoConnect?: boolean;
  onAutoConnectChange?: (checked: boolean) => void;
}

const PlaceForm: React.FC<PlaceFormProps> = ({ place, onSave, onCancel, fromPlaceId, autoConnect = false, onAutoConnectChange }) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [properties, setProperties] = useState('');
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [isRoot, setIsRoot] = useState(false);
  const [linkedUrlType, setLinkedUrlType] = useState<'internal' | 'external' | 'none'>('none');
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [externalUrl, setExternalUrl] = useState('');
  const [externalUrlError, setExternalUrlError] = useState<string | null>(null);
  const [modules] = useState<ModuleItem[]>(getAllModules());
  
  // Item selection state for modules with detail pages
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState<(Person | Novel | Note | KnowledgeCategory | Book)[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [manualItemId, setManualItemId] = useState('');
  const [searchingItems, setSearchingItems] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);

  // Search for items when module changes or search term changes
  useEffect(() => {
    if (!selectedModule || !moduleSupportsDetailPages(selectedModule) || !user) {
      setShowItemSearch(false);
      setItemSearchResults([]);
      setItemSearchTerm('');
      return;
    }

    setShowItemSearch(true);
    
    const searchItems = async () => {
      if (!itemSearchTerm.trim()) {
        setItemSearchResults([]);
        return;
      }

      setSearchingItems(true);
      try {
        const serviceType = getModuleServiceType(selectedModule);
        if (serviceType === 'people') {
          const response = await peopleService.getPeopleByUserId(user.id, itemSearchTerm);
          if (response.success) {
            setItemSearchResults(response.people);
          }
        } else if (serviceType === 'novel') {
          const novels = await NovelService.getUserNovels(user.id, { limit: 50, sort_order: 'DESC' });
          const filtered = novels.filter(novel => 
            novel.title.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
            novel.genre?.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'notes') {
          const response = await NotesService.getUserNotes(user.id, { limit: 50, sort_order: 'DESC' });
          const filtered = response.data.filter(note => 
            note.title.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'knowledge') {
          const response = await KnowledgeCategoryService.getCategories();
          const filtered = response.categories.filter(category => 
            category.category_name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
            category.description?.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'book') {
          const books = await bookService.searchBooks(itemSearchTerm);
          setItemSearchResults(books);
        }
      } catch (error) {
        console.error('Error searching items:', error);
        setItemSearchResults([]);
      } finally {
        setSearchingItems(false);
      }
    };

    const debounceTimer = setTimeout(searchItems, 300);
    return () => clearTimeout(debounceTimer);
  }, [selectedModule, itemSearchTerm, user]);

  useEffect(() => {
    if (place) {
      setName(place.name);
      setDescription(place.description || '');
      setProperties(
        place.properties 
          ? JSON.stringify(place.properties, null, 2)
          : ''
      );
      setIsRoot(place.is_root || false);
      
      // Handle linked URL
      const linkedUrl = place.properties?.linkedUrl;
      if (linkedUrl) {
        const urlType = getUrlType(linkedUrl);
        if (urlType.type === 'internal') {
          setLinkedUrlType('internal');
          
          // Check if it's a detail URL (e.g., /novel/123 or /people/456)
          const detailParse = parseDetailUrl(linkedUrl);
          if (detailParse) {
            setSelectedModule(detailParse.basePath);
            setSelectedItemId(detailParse.itemId || '');
            setManualItemId(detailParse.itemId || '');
          } else {
            setSelectedModule(urlType.path);
            setSelectedItemId('');
            setManualItemId('');
          }
          setExternalUrl('');
        } else {
          setLinkedUrlType('external');
          setExternalUrl(linkedUrl);
          setSelectedModule('');
          setSelectedItemId('');
          setManualItemId('');
        }
      } else {
        setLinkedUrlType('none');
        setSelectedModule('');
        setExternalUrl('');
        setSelectedItemId('');
        setManualItemId('');
      }
    } else {
      setName('');
      setDescription('');
      setProperties('');
      setIsRoot(false);
      setLinkedUrlType('none');
      setSelectedModule('');
      setExternalUrl('');
      setSelectedItemId('');
      setManualItemId('');
    }
  }, [place]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    // Validate external URL if external type is selected
    if (linkedUrlType === 'external' && externalUrl.trim()) {
      const trimmedUrl = externalUrl.trim();
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        setExternalUrlError('External URL must start with http:// or https://');
        return;
      }
      setExternalUrlError(null);
    }

    let parsedProperties: Record<string, any> | undefined;
    if (properties.trim()) {
      try {
        parsedProperties = JSON.parse(properties);
        if (typeof parsedProperties !== 'object' || Array.isArray(parsedProperties)) {
          setPropertiesError('Properties must be a valid JSON object');
          return;
        }
        setPropertiesError(null);
      } catch (error) {
        setPropertiesError('Invalid JSON format');
        return;
      }
    } else {
      parsedProperties = {};
    }

    // Add linked URL to properties
    if (linkedUrlType === 'internal' && selectedModule) {
      let finalUrl = selectedModule;
      
      // If module supports detail pages and an item ID is selected/entered
      if (moduleSupportsDetailPages(selectedModule)) {
        const itemId = selectedItemId || manualItemId.trim();
        if (itemId) {
          finalUrl = `${selectedModule}/${itemId}`;
        }
      }
      
      parsedProperties = { ...parsedProperties, linkedUrl: finalUrl };
    } else if (linkedUrlType === 'external' && externalUrl.trim()) {
      parsedProperties = { ...parsedProperties, linkedUrl: externalUrl.trim() };
    } else if (linkedUrlType === 'none') {
      // Remove linkedUrl if it exists
      if (parsedProperties) {
        delete parsedProperties.linkedUrl;
        // If properties object is empty after removing linkedUrl, set to undefined
        if (Object.keys(parsedProperties).length === 0) {
          parsedProperties = undefined;
        }
      }
    }

    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      properties: parsedProperties,
      is_root: isRoot
    });
  };

  return (
    <div className="memory-palace-form-overlay">
      <div className="memory-palace-form">
        <div className="memory-palace-form-header">
          <h2>{place ? 'Edit Place' : 'Create New Place'}</h2>
          <button className="memory-palace-form-close" onClick={onCancel}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="memory-palace-form-content">
          <div className="memory-palace-form-field">
            <label htmlFor="place-name">Name *</label>
            <input
              id="place-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter place name"
              required
              className="memory-palace-form-input"
            />
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="place-description">Description</label>
            <textarea
              id="place-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter place description"
              rows={4}
              className="memory-palace-form-textarea"
            />
          </div>

          <div className="memory-palace-form-field" style={{ display: 'none' }}>
            <label htmlFor="place-properties">Properties (JSON)</label>
            <textarea
              id="place-properties"
              value={properties}
              onChange={(e) => {
                setProperties(e.target.value);
                setPropertiesError(null);
              }}
              placeholder='{"key": "value"}'
              rows={6}
              className={`memory-palace-form-textarea ${propertiesError ? 'error' : ''}`}
            />
            {propertiesError && (
              <div className="memory-palace-form-error">{propertiesError}</div>
            )}
            <div className="memory-palace-form-hint">
              Optional JSON object to store additional place information
            </div>
          </div>

          <div className="memory-palace-form-field">
            <label>Linked URL</label>
            <div className="memory-palace-form-radio-group">
              <label className="memory-palace-form-radio-label">
                <input
                  type="radio"
                  name="linkedUrlType"
                  value="none"
                  checked={linkedUrlType === 'none'}
                  onChange={(e) => {
                    setLinkedUrlType('none');
                    setSelectedModule('');
                    setExternalUrl('');
                    setExternalUrlError(null);
                  }}
                  className="memory-palace-form-radio"
                />
                <span>None</span>
              </label>
              <label className="memory-palace-form-radio-label">
                <input
                  type="radio"
                  name="linkedUrlType"
                  value="internal"
                  checked={linkedUrlType === 'internal'}
                  onChange={(e) => {
                    setLinkedUrlType('internal');
                    setExternalUrl('');
                    setExternalUrlError(null);
                  }}
                  className="memory-palace-form-radio"
                />
                <span>Internal Module</span>
              </label>
              <label className="memory-palace-form-radio-label">
                <input
                  type="radio"
                  name="linkedUrlType"
                  value="external"
                  checked={linkedUrlType === 'external'}
                  onChange={(e) => {
                    setLinkedUrlType('external');
                    setSelectedModule('');
                  }}
                  className="memory-palace-form-radio"
                />
                <span>External URL</span>
              </label>
            </div>
            
            {linkedUrlType === 'internal' && (
              <div style={{ marginTop: '0.5rem' }}>
                <select
                  value={selectedModule}
                  onChange={(e) => {
                    setSelectedModule(e.target.value);
                    setSelectedItemId('');
                    setManualItemId('');
                    setItemSearchTerm('');
                    setItemSearchResults([]);
                  }}
                  className="memory-palace-form-select"
                >
                  <option value="">Select a module...</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.path}>
                      {module.label}
                    </option>
                  ))}
                </select>
                
                {showItemSearch && selectedModule && (
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#333333' }}>
                      Search and select item (or enter ID manually):
                    </label>
                    <input
                      type="text"
                      value={itemSearchTerm}
                      onChange={(e) => setItemSearchTerm(e.target.value)}
                      placeholder={`Search ${selectedModule === '/people' ? 'people' : selectedModule === '/notes' ? 'notes' : selectedModule === '/knowledge' ? 'knowledge categories' : selectedModule === '/books' ? 'books' : 'novels'}...`}
                      className="memory-palace-form-input"
                      style={{ marginBottom: '0.5rem' }}
                    />
                    
                    {searchingItems && (
                      <div style={{ fontSize: '0.75rem', color: '#666666', marginBottom: '0.5rem' }}>Searching...</div>
                    )}
                    
                    {!searchingItems && itemSearchResults.length > 0 && (
                      <select
                        value={selectedItemId}
                        onChange={(e) => {
                          setSelectedItemId(e.target.value);
                          setManualItemId('');
                        }}
                        className="memory-palace-form-select"
                        style={{ marginBottom: '0.5rem' }}
                      >
                        <option value="">Select an item...</option>
                        {itemSearchResults.map((item) => {
                          const idField = getModuleIdField(selectedModule);
                          const itemId = idField ? (item as any)[idField] : '';
                          let displayName = '';
                          if (selectedModule === '/people') {
                            displayName = (item as Person).name;
                          } else if (selectedModule === '/notes') {
                            displayName = (item as Note).title;
                          } else if (selectedModule === '/knowledge') {
                            displayName = (item as KnowledgeCategory).category_name;
                          } else if (selectedModule === '/books') {
                            displayName = (item as Book).title;
                          } else {
                            displayName = (item as Novel).title;
                          }
                          return (
                            <option key={itemId} value={String(itemId)}>
                              {displayName}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    
                    <div style={{ fontSize: '0.75rem', color: '#666666', marginBottom: '0.5rem' }}>
                      Or enter {selectedModule === '/people' ? 'person' : selectedModule === '/notes' ? 'note' : selectedModule === '/knowledge' ? 'category' : selectedModule === '/books' ? 'book' : 'novel'} ID manually:
                    </div>
                    <input
                      type="text"
                      value={manualItemId}
                      onChange={(e) => {
                        setManualItemId(e.target.value);
                        setSelectedItemId('');
                      }}
                      placeholder={`Enter ${selectedModule === '/people' ? 'person' : selectedModule === '/notes' ? 'note' : selectedModule === '/knowledge' ? 'category' : selectedModule === '/books' ? 'book' : 'novel'} ID`}
                      className="memory-palace-form-input"
                    />
                    <div className="memory-palace-form-hint">
                      Leave empty to link to the module list page
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {linkedUrlType === 'external' && (
              <div style={{ marginTop: '0.5rem' }}>
                <input
                  type="text"
                  value={externalUrl}
                  onChange={(e) => {
                    setExternalUrl(e.target.value);
                    setExternalUrlError(null);
                  }}
                  placeholder="https://example.com"
                  className={`memory-palace-form-input ${externalUrlError ? 'error' : ''}`}
                />
                {externalUrlError && (
                  <div className="memory-palace-form-error">{externalUrlError}</div>
                )}
                <div className="memory-palace-form-hint">
                  Enter a full URL starting with http:// or https://
                </div>
              </div>
            )}
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="place-is-root" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                id="place-is-root"
                type="checkbox"
                checked={isRoot}
                onChange={(e) => setIsRoot(e.target.checked)}
                className="memory-palace-form-checkbox"
              />
              <span>Mark as root</span>
            </label>
            <div className="memory-palace-form-hint">
              Only one place can be marked as root. Setting this will unset the previous root place.
            </div>
          </div>

          {fromPlaceId && onAutoConnectChange && (
            <div className="memory-palace-form-field">
              <label htmlFor="place-auto-connect" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  id="place-auto-connect"
                  type="checkbox"
                  checked={autoConnect}
                  onChange={(e) => onAutoConnectChange(e.target.checked)}
                  className="memory-palace-form-checkbox"
                />
                <span>Create connection from current place</span>
              </label>
              <div className="memory-palace-form-hint">
                Automatically create a connection from the current place to this new place.
              </div>
            </div>
          )}

          <div className="memory-palace-form-actions">
            <button type="button" onClick={onCancel} className="memory-palace-form-btn cancel">
              Cancel
            </button>
            <button type="submit" className="memory-palace-form-btn save">
              {place ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlaceForm;

